// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { SystemDetailsModel } from 'src/app/api/remote-api/.portal.generated/models';
import { APICallGateComponent } from 'src/app/modules/shared/components/api-call-gate.component';
import { ScreenLayoutService } from 'src/app/modules/shared/services/screen-layout-service/screen-layout.service';
import { DnaSyngoviaServerNode } from '../../interfaces/dna-syngovia-node';
import { DnaSyngoviaScreenInteractionService } from '../../services/dna-syngovia-screen-interaction.service';
import { DnaSyngoviaService } from '../../services/dna-syngovia.service';
import { DnaSyngoviaCustomerOverviewComponent } from '../dna-syngovia-customer-overview/dna-syngovia-customer-overview.component';
import { DnaSyngoviaEmptyStateComponent } from '../dna-syngovia-empty-state/dna-syngovia-empty-state.component';
import { DnaSyngoviaHealthcheckComponent } from '../dna-syngovia-healthcheck/dna-syngovia-healthcheck.component';

export enum ActiveTabEType {
  OVERVIEW = 'overview',
  HEALTHCHECK = 'healthcheck',
}

@Component({
  standalone: true,
  imports: [
    APICallGateComponent,
    CommonModule,
    TranslateModule,
    DnaSyngoviaCustomerOverviewComponent,
    DnaSyngoviaHealthcheckComponent,
    DnaSyngoviaEmptyStateComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-dna-syngovia',
  templateUrl: './dna-syngovia.component.html',
  styleUrls: ['./dna-syngovia.component.scss'],
})
export class DnaSyngoviaComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) systemDetails!: SystemDetailsModel;

  protected readonly TAB_OVERVIEW = ActiveTabEType.OVERVIEW;
  protected readonly TAB_HEALTHCHECK = ActiveTabEType.HEALTHCHECK;

  protected activeTab = this.TAB_OVERVIEW;

  protected networkConnections = this.dnaSyngoviaService.getNetworkConnections;
  protected serverHealth = this.dnaSyngoviaService.getServersHealth;

  protected modalPortalVisible = true;
  protected modalMobileVisible = true;
  protected modalPortalConfirmed = localStorage.getItem('@dnaSyngovia');
  protected modalMobileConfirmed = sessionStorage.getItem(
    '@dnaSyngoviaMobileDisclaimer'
  );

  protected selectedServer: DnaSyngoviaServerNode | undefined;
  private readonly unsubscribe = new Subject<void>();

  public constructor(
    protected readonly dnaSyngoviaService: DnaSyngoviaService,
    protected readonly screenLayoutService: ScreenLayoutService,
    private readonly screenInteractionService: DnaSyngoviaScreenInteractionService
  ) {
    this.listenToHealthcheckEvents();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['systemDetails'].currentValue) {
      this.requestNetworkConnections();
      this.requestServersHealth();
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private requestNetworkConnections() {
    this.networkConnections.request({
      SystemMaterialNumber: this.systemDetails.systemMaterialNumber,
      SystemSerialNumber: this.systemDetails.systemSerialNumber,
    });
  }

  private requestServersHealth() {
    this.serverHealth.request({
      SystemMaterialNumber: this.systemDetails.systemMaterialNumber,
      SystemSerialNumber: this.systemDetails.systemSerialNumber,
    });
  }

  public activateTab(activeTab: ActiveTabEType): void {
    this.activeTab = activeTab;
  }

  protected onClickConfirmModalPortal = (): void => {
    this.modalPortalVisible = false;
    localStorage.setItem('@dnaSyngovia', 'Confirm modal');
  };

  protected onClickConfirmModalMobile = (): void => {
    this.modalMobileVisible = false;
    sessionStorage.setItem(
      '@dnaSyngoviaMobileDisclaimer',
      'Confirm mobile modal'
    );
  };

  private listenToHealthcheckEvents(): void {
    this.screenInteractionService
      .onServerHealthcheckChange()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(this.healthcheckChangeSubscriber);
  }

  private readonly healthcheckChangeSubscriber = (
    node: DnaSyngoviaServerNode
  ): void => {
    if (node) {
      this.selectedServer = node;
      this.activateTab(this.TAB_HEALTHCHECK);
      this.scrollTop();
    }
  };

  private scrollTop() {
    window.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }
}
